import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CDNService from '../../../services/CDNService';

const CDNConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [formData, setFormData] = useState({
    provider: '',
    name: '',
    base_url: '',
    api_key: '',
    api_secret: '',
    region: '',
    is_default: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configsRes, providersRes, statusRes] = await Promise.all([
        CDNService.getConfigs(),
        CDNService.getProviders(),
        CDNService.getStatus()
      ]);

      setConfigs(configsRes.data || []);
      setProviders(providersRes.data || []);
      setStatus(statusRes.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load CDN data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConfig) {
        await CDNService.updateConfig(editingConfig.id, formData);
      } else {
        await CDNService.createConfig(formData);
      }
      setShowForm(false);
      setEditingConfig(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to save CDN configuration');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      provider: config.provider,
      name: config.name,
      base_url: config.base_url,
      api_key: '',
      api_secret: '',
      region: config.region || '',
      is_default: config.is_default === 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this CDN configuration?')) {
      return;
    }

    try {
      await CDNService.deleteConfig(id);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to delete CDN configuration');
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const result = await CDNService.testConnection(id);
      alert(result.message || result.error);
    } catch (err) {
      alert(err.message || 'Failed to test connection');
    } finally {
      setTestingId(null);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await CDNService.setDefaultConfig(id);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to set default configuration');
    }
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      name: '',
      base_url: '',
      api_key: '',
      api_secret: '',
      region: '',
      is_default: false
    });
  };

  const selectedProvider = providers.find(p => p.id === formData.provider);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Check if CDN is enabled for this user
  if (status && !status.enabled) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-server fa-3x text-muted mb-3"></i>
                <h4>CDN Configuration Not Available</h4>
                <p className="text-muted">
                  {status.reason === 'NO_SUBSCRIPTION' 
                    ? 'You need an active subscription to access CDN features.'
                    : 'Please upgrade to a plan that includes CDN configuration.'}
                </p>
                <Link to="/pricing" className="btn btn-primary">
                  View Pricing Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="card-title">CDN Configuration</h4>
              <p className="text-muted mb-0">
                Manage your Content Delivery Network settings
              </p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setEditingConfig(null);
                resetForm();
                setShowForm(true);
              }}
            >
              <i className="fas fa-plus me-2"></i>
              Add CDN Config
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {showForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  {editingConfig ? 'Edit CDN Configuration' : 'Add CDN Configuration'}
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Provider *</label>
                      <select
                        className="form-select"
                        name="provider"
                        value={formData.provider}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Provider</option>
                        {providers.map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {provider.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Configuration Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Production CDN"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Base URL *</label>
                    <input
                      type="url"
                      className="form-control"
                      name="base_url"
                      value={formData.base_url}
                      onChange={handleInputChange}
                      required
                      placeholder="https://cdn.example.com"
                    />
                  </div>

                  {selectedProvider?.requires_api_key && (
                    <div className="mb-3">
                      <label className="form-label">API Key {editingConfig && '(leave blank to keep current)'}</label>
                      <input
                        type="password"
                        className="form-control"
                        name="api_key"
                        value={formData.api_key}
                        onChange={handleInputChange}
                        placeholder={editingConfig ? '••••••••' : 'Enter API key'}
                      />
                    </div>
                  )}

                  {selectedProvider?.requires_api_secret && (
                    <div className="mb-3">
                      <label className="form-label">API Secret {editingConfig && '(leave blank to keep current)'}</label>
                      <input
                        type="password"
                        className="form-control"
                        name="api_secret"
                        value={formData.api_secret}
                        onChange={handleInputChange}
                        placeholder={editingConfig ? '••••••••' : 'Enter API secret'}
                      />
                    </div>
                  )}

                  {selectedProvider?.supports_regions && (
                    <div className="mb-3">
                      <label className="form-label">Region</label>
                      <select
                        className="form-select"
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Region</option>
                        {selectedProvider.regions?.map(region => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleInputChange}
                        id="is_default"
                      />
                      <label className="form-check-label" htmlFor="is_default">
                        Set as default configuration
                      </label>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setEditingConfig(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingConfig ? 'Update Configuration' : 'Create Configuration'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              {configs.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-server fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No CDN configurations added yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Provider</th>
                        <th>Base URL</th>
                        <th>Status</th>
                        <th>Default</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configs.map(config => (
                        <tr key={config.id}>
                          <td>{config.name}</td>
                          <td>
                            <span className="badge bg-info">
                              {providers.find(p => p.id === config.provider)?.name || config.provider}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">{config.base_url}</small>
                          </td>
                          <td>
                            <span className={`badge ${config.is_active ? 'bg-success' : 'bg-warning'}`}>
                              {config.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            {config.is_default ? (
                              <span className="badge bg-primary">Default</span>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleSetDefault(config.id)}
                              >
                                Set Default
                              </button>
                            )}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-info"
                                onClick={() => handleTest(config.id)}
                                disabled={testingId === config.id}
                                title="Test Connection"
                              >
                                {testingId === config.id ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <i className="fas fa-plug"></i>
                                )}
                              </button>
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleEdit(config)}
                                title="Edit"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(config.id)}
                                title="Delete"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CDNConfigPage;
